import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Divider from "@mui/material/Divider";
import { useTheme } from "@mui/material/styles";

import { ThemedHistoryIcon } from "assets/CXFIcons/Portal/Dashboard";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { Icon } from "@mui/material";

import CategoriesList from "cxfcomponents/Lists/CategoriesList";

import { getLocalDateTimeString } from "helpers/common";

function RecentFlows({ recentFlows, embedded = false }) {
	const [categories, setCategories] = useState([]);
	const { t } = useTranslation("dashboard");
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === "dark";

	useEffect(() => {
		const mappedCategories = recentFlows?.map((flow) => ({
			id: flow.id,
			color: "dark",
			icon: "",
			name: flow.name,
			description:
				getLocalDateTimeString(flow.updated_date_time || flow.created_date_time) || "",
			route: `/designer/${flow.id}`,
		}));

		setCategories(mappedCategories || []);
	}, [recentFlows]);

	if (!embedded) {
		return (
			<SoftBox height="100%">
				<CategoriesList
					title={t("labels.recent_flows")}
					categories={categories}
					headerIcon={
						<ThemedHistoryIcon sx={{ position: "relative", top: "3px" }} />
					}
					noDataMessage={t("messages.no_recent_activities")}
				/>
			</SoftBox>
		);
	}

	return (
		<SoftBox height="100%">
			<SoftBox
				display="flex"
				alignItems="center"
				justifyContent="space-between"
				mb={2}
			>
				<SoftBox display="flex" alignItems="center" gap={1}>
					<ThemedHistoryIcon sx={{ position: "relative", top: "1px" }} />
					<SoftTypography variant="h6" fontWeight="bold" className="white-to-dark">
						{t("labels.recent_flows")}
					</SoftTypography>
				</SoftBox>
			</SoftBox>

			{categories.length ? (
				<SoftBox>
					{categories.map((flow, index) => (
						<SoftBox key={flow.id}>
							<SoftBox
								component={Link}
								to={flow.route}
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									textDecoration: "none",
									py: 1.5,
									color: "inherit",
								}}
							>
								<SoftBox minWidth={0} pr={2}>
									<SoftTypography
										variant="button"
										fontWeight="medium"
										className="white-to-dark"
										sx={{
											display: "block",
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}}
									>
										{flow.name}
									</SoftTypography>

									<SoftTypography
										variant="caption"
										color="text"
										sx={{ display: "block", mt: 0.35 }}
									>
										{flow.description}
									</SoftTypography>
								</SoftBox>

								<SoftTypography
									variant="button"
									color="text"
									sx={{
										cursor: "pointer",
										lineHeight: 0,
										transition: "all 0.2s cubic-bezier(.34,1.61,.7,1.3)",
										p: 0.5,
										marginRight: "12px",
										"&:hover, &:focus": {
										transform: "translateX(5px)"
										}
									}}
								>
									<Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
								</SoftTypography>
							</SoftBox>

							{index !== categories.length - 1 && (
								<Divider
									sx={{
										m: 0,
										opacity: 0.12,
										borderColor: isDarkMode
											? "rgba(255,255,255,0.08)"
											: "rgba(0,0,0,0.08)",
									}}
								/>
							)}
						</SoftBox>
					))}
				</SoftBox>
			) : (
				<SoftTypography variant="button" color="text">
					{t("messages.no_recent_activities")}
				</SoftTypography>
			)}
		</SoftBox>
	);
}

export default RecentFlows;
